import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Field } from '../components/ui.jsx';
import { ApiError } from '../api/client.js';
import { useAuth, homeFor } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';

const Register = () => {
  useTitle('Create an account');
  const [params] = useSearchParams();
  const { register } = useAuth();
  const { cities } = useMeta();
  const toast = useToast();
  const navigate = useNavigate();

  const [role, setRole] = useState('landlord');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    city: '',
    businessName: ''
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});

    try {
      const user = await register({
        role,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        city: form.city || undefined,
        businessName: role === 'landlord' && form.businessName ? form.businessName.trim() : undefined
      });

      toast.success('Account created. Welcome.');
      navigate(params.get('next') || homeFor(user), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>Create your account</h1>

        <div className="role-picker" role="radiogroup" aria-label="Account type">
          <button
            type="button"
            role="radio"
            aria-checked={role === 'landlord'}
            className={`role-option ${role === 'landlord' ? 'is-current' : ''}`}
            onClick={() => setRole('landlord')}
          >
            <strong>I have a property to let</strong>
            <span>List houses, upload photos and videos, and manage applicants.</span>
          </button>
        </div>

        {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

        <form className="stack" onSubmit={submit}>
          <Field label="Full name" error={errors.name} required>
            <input
              type="text"
              autoComplete="name"
              required
              value={form.name}
              onChange={(event) => set({ name: event.target.value })}
              placeholder="Ngwa Emmanuel"
            />
          </Field>

          <div className="grid-2">
            <Field label="Email" error={errors.email} required>
              <input
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(event) => set({ email: event.target.value })}
              />
            </Field>

            <Field
              label="Phone number"
              hint="We show this to the other party once you are in a conversation."
              error={errors.phone}
              required
            >
              <input
                type="tel"
                autoComplete="tel"
                required
                value={form.phone}
                onChange={(event) => set({ phone: event.target.value })}
                placeholder="+237 6 70 00 00 00"
              />
            </Field>
          </div>

          <div className="grid-2">
            <Field label="Password" hint="At least 8 characters." error={errors.password} required>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={form.password}
                onChange={(event) => set({ password: event.target.value })}
              />
            </Field>

            <Field label="Town" error={errors.city}>
              <select value={form.city} onChange={(event) => set({ city: event.target.value })}>
                <option value="">Choose a town</option>
                {cities.map((city) => (
                  <option key={city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {role === 'landlord' ? (
            <Field
              label="Business name"
              hint="Optional. Shown on your listings instead of your personal name."
              error={errors.businessName}
            >
              <input
                type="text"
                value={form.businessName}
                onChange={(event) => set({ businessName: event.target.value })}
                placeholder="Mbah Family Properties"
              />
            </Field>
          ) : null}

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Creating your account...' : 'Create account'}
          </button>
        </form>

        <p className="auth-alt">
          Already registered? <Link to="/login">Sign in instead</Link>
        </p>
      </div>

      <aside className="auth-aside">
          <>
            <h2>What landlords get</h2>
            <ul className="tick-list">
              <li>Unlimited listings with photos and a walk-through video</li>
              <li>Applications in one queue instead of scattered phone calls</li>
              <li>Applicant details and documents attached to each application</li>
              <li>A verified badge once you upload your ID or business papers</li>
              <li>Views, saves and application counts for every listing</li>
            </ul>
          </>
      </aside>
    </div>
  );
};

export default Register;
